import React, { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { clientPost, errorMessage, isUnauthorized } from '../../lib/clientApi';

const CATEGORIES = [['BILLING', 'Facturation'], ['INTERVENTION', 'Intervention'], ['PLANNING', 'Planning'], ['OTHER', 'Autre']];

export function TicketDialog({ open, onClose, onDone, onUnauthorized }) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('OTHER');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setSubject(''); setCategory('OTHER'); setMessage(''); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) return toast.error("Précisez l'objet de votre demande");
    if (message.trim().length < 5) return toast.error('Décrivez votre demande en quelques lignes');
    setLoading(true);
    try {
      await clientPost('/api/client/tickets', { subject: subject.trim(), category, message: message.trim() });
      onDone();
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, "Impossible d'envoyer votre demande"));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="client-ticket-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-xl">Déposer une réclamation</DialogTitle>
            <DialogDescription className="text-base">Votre demande est enregistrée chez Ptitservices et traitée par l'agence.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-subject" className="text-base">Objet</Label>
            <Input id="ticket-subject" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex. Facture, intervention, planning…" className="h-12 text-base" data-testid="client-ticket-subject-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-base">Catégorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12 text-base" data-testid="client-ticket-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id} className="text-base" data-testid={`client-ticket-category-${id}`}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-message" className="text-base">Message</Label>
            <Textarea id="ticket-message" rows={5} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Décrivez votre demande en quelques lignes." className="text-base" data-testid="client-ticket-message-textarea" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="h-11" onClick={onClose} data-testid="client-ticket-cancel-button">Annuler</Button>
            <Button type="submit" className="h-11" disabled={loading || subject.trim().length < 3 || message.trim().length < 5} data-testid="client-ticket-submit-button">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Envoyer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
